/** Core gameplay data types. All gameplay content is data-driven from these shapes. */

export const enum Res {
  Food = 0,
  Wood = 1,
  Gold = 2,
  Stone = 3,
}
export const RES_COUNT = 4;
export const RES_NAMES = ['Food', 'Wood', 'Gold', 'Stone'] as const;

/** [food, wood, gold, stone] */
export type Cost = [number, number, number, number];

/** Unit/building class flags used for counters, bonuses and tech targeting. */
export const C = {
  Villager: 1 << 0,
  Infantry: 1 << 1,
  Spear: 1 << 2,
  Archer: 1 << 3,
  Cavalry: 1 << 4,
  MountedArcher: 1 << 5,
  Siege: 1 << 6,
  Gunpowder: 1 << 7,
  Ship: 1 << 8,
  Building: 1 << 9,
  Tower: 1 << 10,
  Wall: 1 << 11,
  Elephant: 1 << 12,
  Scout: 1 << 13,
  Unique: 1 << 14,
  Artillery: 1 << 15,
  Heavy: 1 << 16,
  Military: 1 << 17,
  /** Heals friendly units around it (monks). */
  Healer: 1 << 18,
  /** Commandeers enemy buildings and repairs its own side's quickly (engineers). */
  Engineer: 1 << 19,
} as const;

export const AGE_NAMES = ['Settlement Age', 'Feudal Age', 'Castle Age', 'Imperial Age', 'Industrial Age'] as const;

export interface Bonus {
  /** Class mask the bonus applies against. */
  vs: number;
  dmg: number;
}

export type ProjectileKind = 'arrow' | 'bolt' | 'stone' | 'ball' | 'bullet' | 'shell';

export interface AttackDef {
  melee: number;
  pierce: number;
  /** Range in tiles; 0 means melee contact range. */
  range: number;
  minRange?: number;
  /** Seconds between attacks. */
  cooldown: number;
  bonuses?: Bonus[];
  projectile?: ProjectileKind;
  /** Projectile speed in tiles per second. */
  projectileSpeed?: number;
  /** Splash radius in tiles. */
  splash?: number;
  /** Accuracy percentage (100 = always hits target). */
  accuracy?: number;
}

export type UnitModel =
  | 'villager'
  | 'monk'
  | 'engineer'
  | 'infantry'
  | 'spear'
  | 'archer'
  | 'crossbow'
  | 'cavalry'
  | 'heavyCavalry'
  | 'horseArcher'
  | 'scout'
  | 'gunner'
  | 'ram'
  | 'mangonel'
  | 'trebuchet'
  | 'cannon'
  | 'elephant'
  | 'ship';

export interface UnitDef {
  key: string;
  name: string;
  classes: number;
  cost: Cost;
  /** Seconds. */
  trainTime: number;
  hp: number;
  armor: { melee: number; pierce: number };
  attack?: AttackDef;
  /** Tiles per second. */
  speed: number;
  /** Line of sight in tiles. */
  los: number;
  pop: number;
  age: number;
  trainedAt: string;
  /** Collision radius in tiles. */
  radius: number;
  model: UnitModel;
  /** Weapon/visual variant hint for the renderer. */
  weapon?: 'sword' | 'spear' | 'bow' | 'crossbow' | 'musket' | 'axe' | 'club' | 'staff' | 'hammer' | 'none';
  helmet?: 'none' | 'cap' | 'kettle' | 'great' | 'kabuto' | 'turban' | 'feather' | 'tricorn';
  shield?: boolean;
  /** Civilisation that exclusively owns this unit. */
  civ?: string;
  /** Gathering rates per second for villagers, by resource. */
  gatherRates?: [number, number, number, number];
  carryCapacity?: number;
  /** Transport ships: room for this many foot units (mounted units and siege take two places). */
  garrison?: number;
  description: string;
}

export interface BuildingModel {
  w: number;
  d: number;
  wallH: number;
  roof: 'gable' | 'hip' | 'flat' | 'cone' | 'dome' | 'pyramid' | 'none';
  tower?: number;
  chimney?: boolean;
  fence?: boolean;
  field?: boolean;
  keep?: boolean;
  banner?: boolean;
  /** Visual material hint. */
  material?: 'timber' | 'stone' | 'brick' | 'plaster';
}

export interface BuildingDef {
  key: string;
  name: string;
  cost: Cost;
  /** Seconds for one villager. */
  buildTime: number;
  hp: number;
  armor: { melee: number; pierce: number };
  /** Footprint in tiles (square). */
  size: number;
  los: number;
  pop: number;
  age: number;
  dropoff?: number[];
  trains?: string[];
  researches?: string[];
  attack?: AttackDef;
  /** Food amount for farms. */
  foodAmount?: number;
  model: BuildingModel;
  /** Maximum count per player (0 = unlimited). */
  limit?: number;
  classes: number;
  civ?: string;
  /** Room for this many foot units and villagers sheltering inside. */
  garrison?: number;
  /** Whether villagers can construct it (false for start-only buildings). */
  buildable?: boolean;
  description: string;
}

export type ResourceModel = 'tree' | 'pine' | 'palm' | 'berry' | 'gold' | 'stone' | 'deer' | 'sheep' | 'fish';

export interface ResourceDef {
  key: string;
  name: string;
  res: number;
  amount: number;
  radius: number;
  model: ResourceModel;
  /** Occupies its tile (nothing can be built there; before rules v6 nothing could walk there either). */
  blocking: boolean;
  /** Rules v6: units can walk through it, slowed (trees: forests are passable). */
  walkable?: boolean;
}

export type StatKey =
  | 'hp'
  | 'meleeAttack'
  | 'pierceAttack'
  | 'meleeArmor'
  | 'pierceArmor'
  | 'range'
  | 'los'
  | 'speedPct'
  | 'trainTimePct'
  | 'costPct'
  | 'gatherPct'
  | 'buildHpPct'
  | 'attackSpeedPct'
  | 'carry'
  | 'farmFood'
  | 'researchTimePct'
  | 'buildTimePct'
  | 'bonusDmgPct'
  | 'tradePct'
  | 'hpPct'
  | 'accuracy'
  | 'huntPct'
  | 'extraArrows';

export interface Effect {
  /** Target class mask (any match) or explicit def keys. */
  classes?: number;
  keys?: string[];
  /** For gatherPct: resource index affected (omit for all). */
  res?: number;
  stat: StatKey;
  value: number;
}

export interface TechDef {
  key: string;
  name: string;
  cost: Cost;
  /** Seconds. */
  time: number;
  age: number;
  researchedAt: string;
  requires?: string[];
  effects: Effect[];
  /** Advances the researching player's age to this value. */
  advancesTo?: number;
  civ?: string;
  description: string;
}

export interface CivDef {
  key: string;
  name: string;
  /** Historical framing for the faction. */
  setting: string;
  specialisation: string[];
  bonuses: string[];
  weaknesses: string[];
  effects: Effect[];
  uniqueUnits: string[];
  uniqueTechs: string[];
  /** Units/buildings this civ cannot access. */
  disabled: string[];
  /** Architectural palette. */
  architecture: {
    wall: number;
    wallDark: number;
    roof: number;
    trim: number;
    roofStyle: 'gable' | 'hip' | 'flat' | 'dome' | 'pyramid' | 'pagoda';
  };
  uniform: number;
  /** Starting resource adjustments. */
  startBonus?: Partial<Record<'food' | 'wood' | 'gold' | 'stone', number>>;
}
